import React, { useEffect, useState, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Eye, EyeSlash, Money, Global, BitcoinConvert } from "iconsax-reactjs";
import {
    FundWalletBold,
    SendMoneyBold,
    DownloadStatement,
    SendSquare,
    Loan,
    ConvertIcon,
} from "../../asset/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const fmt = (n) =>
    Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const fmtCompact = (n) =>
    new Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(n || 0));

export default function WalletBalanceCard({
    accountLoading,
    isBusinessApproved,
    isCryptoWallet,
    bankName,
    accountNumber,
    overduePayments,
    symbol,
    isCrypto,
    totalBalance,
    creditBalance,
    walletBalance,
    isUsd, // <— when true, only Wallet slide is available
    onFund,
    onConvert,
    onGenerateStatement,
    onSelectTransfer,
    onPay,
    onLoan,
}) {
    const navigate = useNavigate();
    const [showBalance, setShowBalance] = useState(true);
    const [showSendDropdown, setShowSendDropdown] = useState(false);

    // always use the first available if nothing is selected
    const wallets = useSelector((s) => s.wallets || []);
    const [slide, setSlide] = useState(0);
    const sendDropdownRef = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (sendDropdownRef.current && !sendDropdownRef.current.contains(e.target)) {
                setShowSendDropdown(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const canConvert = (wallets || []).length > 1;

    // Lock to Wallet slide when crypto OR USD
    useEffect(() => {
        if (isCryptoWallet || isUsd) setSlide(1);
    }, [isCryptoWallet, isUsd]);

    const wallet = walletBalance ?? 0;
    const credit = creditBalance ?? 0;
    const total = totalBalance ?? 0;

    const primaryAmount = slide === 0 ? total : slide === 1 ? wallet : credit;

    const sendOptions = useMemo(() => {
        if (isCryptoWallet) {
            return [
                {
                    title: `Send ${symbol} to a wallet`,
                    subtitle: `Send ${symbol} to any crypto wallet address`,
                    icon: <BitcoinConvert size="20" color="#292D32" />,
                    type: "wallet",
                },
                {
                    title: `Send ${symbol} to fiat`,
                    subtitle: `Send ${symbol} to other fiat currencies`,
                    icon: <Money size="20" color="#292D32" />,
                    type: "fiat",
                },
            ];
        }
        return [
            {
                title: "Same currency transfer",
                subtitle: "Send money within the same currency",
                icon: <Money size="20" color="#292D32" />,
                type: "same",
            },
            {
                title: "Cross-border transfer",
                subtitle: "Send money between different currencies",
                icon: <Global size="20" color="#9CA3AF" />,
                type: "cross",
            },
        ];
    }, [isCryptoWallet, symbol]);

    const headerTitle = isCryptoWallet
        ? "Wallet Balance"
        : slide === 0
        ? "Total Balance"
        : slide === 1
        ? "Wallet Balance"
        : "Credit Balance";

    const renderSubline = () => {
        if (!isCryptoWallet && slide === 0) {
            const walletFull = `${symbol}${fmt(wallet)}`;
            const creditFull = `${symbol}${fmt(credit)}`;
            const walletShort = `${symbol}${fmtCompact(wallet)}`;
            const creditShort = `${symbol}${fmtCompact(credit)}`;
            return (
                <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[#6B7280]">
                    <div className="flex items-center gap-1">
                        <span>Wallet:</span>
                        <span className="text-[#101828]" title={walletFull} aria-label={walletFull}>
                            {walletShort}
                        </span>
                    </div>
                    <span className="hidden sm:inline text-[#D0D5DD]">•</span>
                    <div className="flex items-center gap-1">
                        <span>Credit:</span>
                        <span className="text-[#101828]" title={creditFull} aria-label={creditFull}>
                            {creditShort}
                        </span>
                    </div>
                </div>
            );
        }

        if (!isCryptoWallet && slide === 1) {
            return (
                <div className="mt-2 text-center text-[#383B56]">
                    {bankName || "Tradevu wallet"} •{" "}
                    <span className="font-medium">{accountNumber || "—"}</span>
                </div>
            );
        }

        if (isCryptoWallet && slide === 1) {
            return (
                <div className="mt-2 text-center text-[#383B56]">
                    Crypto wallet
                </div>
            );
        }
        if (!isCryptoWallet && slide === 2) {
            return (
                <div className="mt-2 text-center text-[#383B56]">
                    <span className="font-semibold">{overduePayments}</span> Overdue repayments
                </div>
            );
        }
        return null;
    };

    const Actions = () => {
        if (isCryptoWallet || slide === 1) {
            return (
                <>
                    <Action icon={<FundWalletBold />} label="Fund" onClick={onFund} />
                    <div className="relative" ref={sendDropdownRef}>
                        <Action
                            icon={<SendMoneyBold />}
                            label="Send"
                            onClick={() => {
                                if (!isBusinessApproved) return;
                                setShowSendDropdown((p) => !p);
                            }}
                        />
                        {showSendDropdown && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 !z-50 w-[min(90vw,360px)] max-w-[90vw] bg-white shadow-dropdown rounded-xl border border-[#F1F1F1] mt-2">
                                {sendOptions.map(({ title, subtitle, icon, type, comingSoon }) => (
                                    <div
                                        key={type}
                                        className={`flex items-start border m-2 rounded-lg gap-3 p-2 ${
                                            comingSoon
                                                ? "opacity-50 cursor-not-allowed"
                                                : "hover:bg-[#F9FAFB] cursor-pointer"
                                        }`}
                                        onClick={() => !comingSoon && onSelectTransfer?.(type)}
                                    >
                                        <div className="w-5 h-5 mt-1">{icon}</div>
                                        <div className="flex-1">
                                            <p className="flex items-center gap-2 font-semibold text-main">
                                                {title}
                                                {comingSoon && (
                                                    <span className="text-xs bg-[#F4EBFF] text-[#6941C6] font-medium px-2 py-1 rounded-full">
                                                        Coming soon
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-[#6B7280] text-sm">{subtitle}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {isCrypto ? (
                        <Action
                            icon={<DownloadStatement />}
                            label="Statement"
                            onClick={onGenerateStatement}
                        />
                    ) : (
                        <Action
                            icon={<ConvertIcon />}
                            label="Convert"
                            onClick={() => canConvert && navigate("/convert-currency")}
                            disabled={!canConvert}
                            title={!canConvert ? "Open another wallet to convert currencies" : ""}
                        />
                    )}
                </>
            );
        }

        if (slide === 0) {
            return (
                <>
                    <Action icon={<FundWalletBold />} label="Fund" onClick={onFund} />
                    <Action icon={<SendSquare />} label="Pay" onClick={onPay} />
                    {/* <Action
                        icon={<DownloadStatement />}
                        label="Statement"
                        onClick={onGenerateStatement}
                    /> */}
                    <Action
                        icon={<ConvertIcon />}
                        label="Convert"
                        onClick={() => canConvert && navigate("/convert-currency")}
                        disabled={!canConvert}
                        title={!canConvert ? "Open another wallet to convert currencies" : ""}
                    />
                </>
            );
        }
        return (
            <>
                <Action icon={<SendSquare />} label="Pay" onClick={onPay} />
            <Action icon={<Loan />} label="Loan" onClick={onLoan} />
            </>
        );
    };

    // Only show the wallet dot for crypto or USD
    const dots = isCryptoWallet || isUsd ? [1] : [0, 1, 2];

    return (
        <div className="col-span-1 px-1 pt-1 pb-0 md:pb-1 bg-white rounded-2xl relative group w-full min-w-0 overflow-visible">
            <div className="grid h-full grid-rows-[53%_minmax(96px,1fr)_auto] md:grid-rows-[48%_1fr_auto]">
                <div className="relative z-30 h-full w-full max-w-full overflow-visible rounded-xl bg-[#F2F3FC] p-2 group">
                    <div className="h-full w-full max-w-full min-w-0 flex flex-col items-center justify-start">
                        <div className="mt-2 flex items-center justify-center w-full max-w-full px-3">
                            <div className="text-[14px] font-[500] text-[#161827] truncate max-w-[80%]">
                                {headerTitle}
                            </div>
                            <button
                                onClick={() => setShowBalance((v) => !v)}
                                className="ml-3 shrink-0 focus:outline-none"
                            >
                                {showBalance ? (
                                    <EyeSlash size="20" color="#27293F" />
                                ) : (
                                    <Eye size="20" color="#27293F" />
                                )}
                            </button>
                        </div>

                        <div className="mb-2 flex items-end justify-center gap-2 min-h-[44px] leading-none w-full max-w-full px-3">
                            <div className="w-full text-center whitespace-nowrap overflow-hidden text-ellipsis">
                                {accountLoading ? (
                                    <div className="h-10 w-48 bg-gray-200 animate-pulse rounded-md mx-auto" />
                                ) : showBalance ? (
                                    isCrypto && (isCryptoWallet || slide === 1) ? (
                                        <span className="text-[clamp(24px,5vw,32px)] font-[450] text-[#161827]">
                                            {fmt(primaryAmount)}
                                            <span className="ml-1 text-[clamp(18px,3.8vw,24px)] font-[350] text-[#383B56]">
                                                {symbol}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-[clamp(24px,5vw,32px)] font-[450] text-[#161827]">
                                            <span className="mr-1 text-[clamp(24px,5vw,32px)] font-[350]">
                                                {symbol}
                                            </span>
                                            {fmt(primaryAmount).split(".")[0]}
                                            <span className="text-[clamp(18px,3.8vw,24px)] font-[350] text-[#6B7280]">
                                                .{fmt(primaryAmount).split(".")[1]}
                                            </span>
                                        </span>
                                    )
                                ) : (
                                    <>•••••••••••••</>
                                )}
                            </div>
                        </div>

                        <div className="min-h-[22px] w-full max-w-full px-3 overflow-hidden text-ellipsis">
                            {renderSubline()}
                        </div>

                        {/*  Hide carousel buttons for USD and for crypto */}
                        {!isCryptoWallet && !isUsd && (
                            <>
                                <button
                                    type="button"
                                    aria-label="Previous"
                                    onClick={() => setSlide((s) => (s + 2) % 3)}
                                    className="absolute left-2 bottom-0 z-30 grid h-10 w-10 translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white shadow-[0_12px_24px_-6px_rgba(16,24,40,0.16)] opacity-0 pointer-events-none group-hover:!opacity-100 group-hover:!pointer-events-auto"
                                >
                                    <ChevronLeft className="pointer-events-none h-5 w-5 text-[#383B56]" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Next"
                                    onClick={() => setSlide((s) => (s + 1) % 3)}
                                    className="absolute right-2 bottom-0 z-30 grid h-10 w-10 translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white shadow-[0_12px_24px_-6px_rgba(16,24,40,0.16)] opacity-0 pointer-events-none group-hover:!opacity-100 group-hover:!pointer-events-auto"
                                >
                                    <ChevronRight className="pointer-events-none h-5 w-5 text-[#383B56]" />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="px-3 sm:px-5 grid items-center relative !z-20 w-full">
                    <div className="flex items-center justify-center gap-4 sm:gap-8 w-full">
                        <Actions />
                    </div>
                </div>

                <div className="mt-2 mb-3 sm:mt-3 sm:mb-3 flex items-center justify-center gap-2 shrink-0 pb-[env(safe-area-inset-bottom)]">
                    {dots.map((i) => (
                        <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                i === slide ? "w-6 bg-[#6F739B]" : "w-1.5 bg-gray-300"
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function Action({ icon, label, onClick, disabled = false, title = "" }) {
    return (
        <div className="flex flex-col items-center gap-1 px-2 py-2 sm:px-4">
            <button
                onClick={onClick}
                disabled={disabled}
                title={title}
                className={`w-12 h-12 sm:w-16 sm:h-16 border-solid border-[#F2F3FC] rounded-full bg-[#FBFCFE] flex items-center justify-center ${
                    disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
                {React.cloneElement(icon, { className: "w-6 h-6 sm:w-8 sm:h-8" })}
            </button>
            <span className="text-[10px] sm:text-sm text-[#101828]">{label}</span>
        </div>
    );
}
